import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import { getAdminCatalogExpansions } from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { ArchiveExpansionButton } from './-components/archive-expansion-button'
import { CreateExpansionDialog } from './-components/create-expansion-dialog'
import { DebouncedSearchInput } from './-components/debounced-search-input'
import { EditExpansionDialog } from './-components/edit-expansion-dialog'
import { FilterSelect } from './-components/filter-select'
import { ResetFiltersButton } from './-components/reset-filters-button'

const EXPANSION_KINDS = [
	'booster',
	'starter',
	'extra_booster',
	'premium_booster',
	'promo',
	'other'
] as const

export const Route = createFileRoute('/expansions')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		direction: z.enum(['asc', 'desc']).optional(),
		kind: z.enum(EXPANSION_KINDS).optional(),
		limit: z.coerce.number().int().min(10).max(100).optional(),
		offset: z.coerce.number().int().nonnegative().optional(),
		q: z.string().optional(),
		sortBy: z.enum(['code', 'kind', 'releaseDate', 'series']).optional()
	}),
	component: ExpansionsPage
})

function ExpansionsPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const limit = search.limit ?? 50
	const offset = search.offset ?? 0
	const sortBy = search.sortBy ?? 'code'
	const direction = search.direction ?? 'asc'
	const expansionsQuery = useQuery({
		queryKey: ['catalog', 'expansions', search],
		queryFn: () =>
			getAdminCatalogExpansions({
				query: {
					direction,
					kind: search.kind,
					limit,
					offset,
					q: search.q,
					sortBy
				}
			}).then((response) => response.data)
	})
	const data = expansionsQuery.data?.data ?? []
	const hasActiveFilters = Boolean(search.kind || search.q)
	const columns: DataTableColumn<(typeof data)[number], typeof sortBy>[] = [
		{
			id: 'code',
			header: t('common:expansion_code'),
			cell: (item) => <span className='font-medium'>{item.code}</span>,
			sortKey: 'code',
			width: 180
		},
		{
			id: 'kind',
			header: t('common:expansion_kind'),
			cell: (item) => <Badge variant='secondary'>{item.kind}</Badge>,
			sortKey: 'kind',
			width: 190
		},
		{
			id: 'series',
			header: t('common:series'),
			cell: (item) => item.series ?? '—',
			sortKey: 'series',
			flex: 1
		},
		{
			id: 'releaseDate',
			header: t('common:release_date'),
			cell: (item) => item.releaseDate ?? '—',
			sortKey: 'releaseDate',
			width: 150
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end gap-2'>
					<EditExpansionDialog expansion={item} />
					<ArchiveExpansionButton id={item.id} />
				</div>
			),
			pinned: 'right',
			width: 220,
			minWidth: 220,
			maxWidth: 220,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: Route.fullPath, search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='font-medium text-primary text-sm'>
						{t('common:catalog')}
					</p>
					<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
						{t('common:expansions')}
					</h1>
				</div>
				<div>
					<CreateExpansionDialog />
				</div>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search')}
					className='w-full sm:w-60'
					onValueChange={(q) =>
						updateSearch({ offset: 0, q: q || undefined })
					}
					placeholder={t('common:search')}
					value={search.q ?? ''}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_expansion_kind')}
					items={[
						{
							label: t('common:all_expansion_kinds'),
							value: 'all'
						},
						...EXPANSION_KINDS.map((value) => ({
							label: value,
							value
						}))
					]}
					label={t('common:expansion_kind')}
					onValueChange={(value) =>
						updateSearch({
							kind: EXPANSION_KINDS.find(
								(item) => item === value
							),
							offset: 0
						})
					}
					value={search.kind ?? 'all'}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() =>
						updateSearch({
							kind: undefined,
							offset: 0,
							q: undefined
						})
					}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-17rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ offset: pageIndex * limit }),
					onPageSizeChange: (nextLimit) =>
						updateSearch({ limit: nextLimit, offset: 0 }),
					pageCount: Math.ceil(
						(expansionsQuery.data?.meta.total ?? 0) / limit
					),
					pageIndex: Math.floor(offset / limit),
					pageSize: limit
				}}
				sort={{
					onSortChange: (next) =>
						updateSearch({
							direction: next.direction,
							offset: 0,
							sortBy: next.by ?? 'code'
						}),
					state: { by: sortBy, direction }
				}}
				state={{
					empty: t('common:empty_expansions'),
					error: expansionsQuery.isError ? (
						<QueryErrorState
							message={t('common:catalog_load_error')}
							onRetry={() => expansionsQuery.refetch()}
						/>
					) : (
						false
					),
					loading: expansionsQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
